"use client";

import { ChevronDownIcon } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useFormatDate } from "@/hooks/useFormatDate";

export function DatePicker({
  value,
  disabled,
  className,
  minDate,
  isDateDisabled,
  onChange,
  placeholder,
}: {
  value?: Date;
  disabled?: boolean;
  className?: string;
  minDate?: Date;
  isDateDisabled?: (date: Date) => boolean;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const { formatDate } = useFormatDate();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          id="date"
          className={cn("justify-between font-normal", className)}
        >
          {value ? (
            formatDate(value)
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronDownIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
        <Calendar
          disabled={(date) => {
            const dateMidnight = new Date(date);
            dateMidnight.setHours(0, 0, 0, 0);
            if (minDate) {
              const minMidnight = new Date(minDate);
              minMidnight.setHours(0, 0, 0, 0);
              if (dateMidnight < minMidnight) return true;
            }
            if (isDateDisabled && isDateDisabled(dateMidnight)) return true;
            return false;
          }}
          mode="single"
          selected={value}
          captionLayout="dropdown"
          toYear={new Date().getFullYear() + 20}
          onSelect={(date) => {
            setOpen(false);
            onChange?.(date);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
